import { ExternalReportViewerDataType } from 'app/extreport/ExternalReportViewer';
import { AnyElementsViewerDataType, ElementViewerDataType, IViewerFiltersData } from 'components/element-viewer';
import { FilterType } from 'components/external-reference/Filter';
import { TileInfo } from 'app/home/index.functions';
import { DashboardElementType } from 'tools/types';
import { IPartsVisibility } from 'components/element-viewer/ControlsPanel';
import React, { useEffect, useMemo, useState } from 'react';
import { BlockType, ComponentSettingsManageFilters } from '@report-content';
import { buildManageFiltersBlock } from 'app/editor/report';
import useReportContentSettings from 'components/report-content/hooks/useReportContentSettings';
import useBookmarks, {
    IBookmarkElementContext,
    IBookmarksContext,
} from 'components/external-reference/hooks/useBookmarks';
import { SiblingTabType } from 'app/extreport/viewer/SiblingTabsMenu';
import { createPortal } from 'react-dom';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import TopPanel from './TopPanel';
import { ReportViewerDataType } from 'app/report/ReportViewer';
import { useInitialData } from 'components/report-content/components/manage-filters/helpers/useInitialData';
import useReportFiltersForReference from 'app/report/useReportFiltersForReference';
import { formatElementDashboardName } from 'tools/tools';
import { escape } from 'lodash';

interface ITopPanelWithBookmarkProps {
    viewerExtraData: AnyElementsViewerDataType;
    viewerData?: ElementViewerDataType;
    filters: Array<FilterType>;
    tile: TileInfo;
    openElement: (type: DashboardElementType, elementId: number, segmentValueId: number) => void;
    onBookmarkChange: (timestamp: number, bookmarkId: number) => void;
    onFavoritesChange: () => void;
    customOnTitleClick?: boolean;
    partsVisibility?: IPartsVisibility;
}

function usePanelBookmark<T extends IViewerFiltersData>(
    onBookmarkChange: (timestamp: number, bookmarkId: number) => void,
    filters: Array<FilterType>,
    siblingTabs: Array<SiblingTabType>,
    viewerFiltersData: T,
    elementContext: IBookmarkElementContext,
    handleApplyBookmark?: (bookmarkId: number) => void,
): IBookmarksContext {
    const {
        bookmarks,
        APIUpdateBookmark,
        APISaveAsNewBookmark,
        APIDeleteBookmark,
        APIDuplicateBookmark,
        APISortBookmarks,
        APIIsLoading,
    } = useBookmarks(viewerFiltersData, elementContext, handleApplyBookmark);

    const [lastBookmark, setLastBookmark] = useState(bookmarks?.[0] ?? null);
    const [loaded, setLoaded] = useState(false);
    useEffect(() => {
        if (JSON.stringify(lastBookmark) == JSON.stringify(bookmarks?.[0] ?? {})) {
            return;
        }
        setLastBookmark(bookmarks?.[0] ?? null);
    }, [JSON.stringify(bookmarks)]);

    useEffect(() => {
        if (lastBookmark == null) {
            return;
        }
        if (!loaded) {
            setLoaded(true);
            return;
        }
        onBookmarkChange(Date.now(), lastBookmark.id);
    }, [lastBookmark]);

    const isInternalReport = elementContext.elementType == 'internal report';
    const currentShowIcon = filters.length + siblingTabs.length > 0;
    const [shownIconsByElement, setShownIconsByElement] = useState<Record<number, boolean>>({});

    useEffect(() => {
        if (isInternalReport && currentShowIcon && !shownIconsByElement[elementContext.elementId]) {
            setShownIconsByElement((prev) => ({ ...prev, [elementContext.elementId]: true }));
        }
    }, [currentShowIcon, elementContext.elementId]);

    const showIcon = isInternalReport
        ? currentShowIcon || (shownIconsByElement[elementContext.elementId] ?? false)
        : currentShowIcon;

    return {
        showIcon: showIcon,
        bookmarks: bookmarks,
        filters: filters,
        siblingTabs: siblingTabs,
        selectedReferenceId: 0,
        APIUpdateBookmark: APIUpdateBookmark,
        APISaveAsNewBookmark: APISaveAsNewBookmark,
        APIDeleteBookmark: APIDeleteBookmark,
        APIDuplicateBookmark: APIDuplicateBookmark,
        APISortBookmarks: APISortBookmarks,
        APIIsLoading: APIIsLoading,
    };
}

function InternalReportPanel({
    viewerExtraData,
    viewerData,
    tile,
    openElement,
    onBookmarkChange,
    onFavoritesChange,
    customOnTitleClick,
    partsVisibility,
}: ITopPanelWithBookmarkProps &
    Required<Pick<ITopPanelWithBookmarkProps, 'viewerData'>> & { viewerExtraData: ReportViewerDataType }) {
    const [manageFiltersBlock, setManageFiltersBlock] = useState<BlockType<ComponentSettingsManageFilters>>(
        buildManageFiltersBlock(viewerExtraData.rendererFilters, viewerExtraData.datasetFields),
    );
    useEffect(() => {
        setManageFiltersBlock(buildManageFiltersBlock(viewerExtraData.rendererFilters, viewerExtraData.datasetFields));
    }, [viewerExtraData?.rendererFilters, viewerExtraData.datasetFields]);

    const { settings } = useReportContentSettings(
        'view',
        viewerData.elementData.row.elementId,
        viewerData.elementData.row.segmentValueId ?? 0,
        [],
        '',
        0,
        false,
        [],
        [],
        viewerData.segmentData,
    );

    const { initialData } = useInitialData(settings, manageFiltersBlock.component);
    const manageFiltersSettings = useMemo(() => manageFiltersBlock.component.settings, [manageFiltersBlock.component]);

    const { filters } = useReportFiltersForReference(
        settings.segmentValues,
        settings.segmentValueId,
        manageFiltersSettings,
        initialData,
    );

    const bookmarksContext = usePanelBookmark(
        onBookmarkChange,
        filters,
        [],
        {
            bookmarksList: viewerExtraData.bookmarksList,
            filtersList: filters,
            liveFilters: true,
            selectedBookmarkId: 0,
        },
        {
            elementId: viewerData.elementData.row.elementId,
            elementType: 'internal report',
            segmentData: viewerData.segmentData,
        },
    );

    return (
        <>
            {bookmarksContext.APIIsLoading &&
                createPortal(<LoadingPlaceholder relativeToParent sx={{ zIndex: 90000 }} />, document.body)}
            <TopPanel
                partsVisibility={partsVisibility}
                customOnTitleClick={customOnTitleClick}
                bookmarksContext={bookmarksContext}
                viewerData={viewerData}
                tileName={tile.element_dashboard_name ?? ''}
                openElement={openElement}
                onFavoritesChange={onFavoritesChange}
            />
        </>
    );
}
function ExternalReportPanel({
    viewerExtraData,
    viewerData,
    filters,
    tile,
    openElement,
    onBookmarkChange,
    onFavoritesChange,
    customOnTitleClick,
    partsVisibility,
}: ITopPanelWithBookmarkProps &
    Required<Pick<ITopPanelWithBookmarkProps, 'viewerData'>> & { viewerExtraData: ExternalReportViewerDataType }) {
    const siblingTabs = viewerExtraData.tabsData.siblingTabs;

    const bookmarksContext = usePanelBookmark(
        onBookmarkChange,
        filters,
        siblingTabs,
        viewerExtraData.filtersData.data,
        {
            elementId: viewerData.elementData.row.elementId,
            elementType: viewerData.elementData.row.type,
            segmentData: viewerData.segmentData,
        },
    );
    return (
        <>
            {bookmarksContext.APIIsLoading &&
                createPortal(<LoadingPlaceholder relativeToParent sx={{ zIndex: 90000 }} />, document.body)}
            <TopPanel
                partsVisibility={partsVisibility}
                customOnTitleClick={customOnTitleClick}
                bookmarksContext={bookmarksContext}
                viewerData={viewerData}
                tileName={tile.element_dashboard_name ?? ''}
                openElement={openElement}
                onFavoritesChange={onFavoritesChange}
            />
        </>
    );
}

export default function TopPanelWithBookmark({ viewerData, ...rest }: ITopPanelWithBookmarkProps) {
    if (viewerData && viewerData.elementData.row.type == 'external report') {
        return (
            <ExternalReportPanel
                {...rest}
                viewerData={viewerData}
                viewerExtraData={rest.viewerExtraData as ExternalReportViewerDataType}
            />
        );
    } else if (viewerData && viewerData.elementData.row.type == 'internal report') {
        return (
            <InternalReportPanel
                {...rest}
                viewerData={viewerData}
                viewerExtraData={rest.viewerExtraData as ReportViewerDataType}
            />
        );
    }
    return (
        <TopPanel
            viewerData={viewerData}
            {...rest}
            tileName={
                undefined !== rest.tile.element_dashboard_name_with_formatting &&
                rest.tile.element_dashboard_name_with_formatting > ''
                    ? formatElementDashboardName(rest.tile.element_dashboard_name_with_formatting)
                    : escape(rest.tile.element_dashboard_name ?? '')
            }
        />
    );
}
